import React, { useState, useRef } from 'react'
import { Box, Tabs, Tab, Typography, Button, Tooltip } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import MenusTab from './MenusTab'
import CategoriesTab from './CategoriesTab'
import ItemsTab from './ItemsTab'
import AdvancedModifierStudio from './AdvancedModifierStudio'
import { useTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import { getRestaurantDetail } from '../apollo'
import { GET_RESTAURANT_ITEMS, GET_ADVANCED_MENUS } from '../apollo/advancedMenuQueries'
import AddIcon from '@mui/icons-material/AddRounded'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import MenuPreviewDialog from '../components/AdvancedMenu/MenuPreviewDialog'
import { mw, segmentedTabsSx, countBadgeSx, displaySx, aiBtnSx, pillBtnSx, pillBtnPrimarySx } from './menuWorkspaceStyles'
import { menuWorkspaceTheme } from './menuWorkspaceTheme'

const GET_RESTAURANT_CATEGORIES_COUNT = gql`${getRestaurantDetail}`

const AdvancedMenus = () => {
  const { t } = useTranslation()
  const [currentTab, setCurrentTab] = useState(0)
  // Preview: null = closed, '' = where customers land, else a menu id.
  const [previewMenuId, setPreviewMenuId] = useState(null)
  const openCreateRef = useRef(null)
  const openAIRef = useRef(null)
  const restaurantId = localStorage.getItem('restaurantId')
  // Breadcrumb only — the same value the sidebar shows.
  const restaurantName = localStorage.getItem('restaurantName')

  const { data: catData, refetch: refetchCats } = useQuery(GET_RESTAURANT_CATEGORIES_COUNT, {
    variables: { id: restaurantId }, skip: !restaurantId
  })
  const { data: itemsData, refetch: refetchItems } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const { data: menusData, refetch: refetchMenus } = useQuery(GET_ADVANCED_MENUS, {
    variables: { restaurantId }, skip: !restaurantId
  })

  // AI menu upload finishes asynchronously; refresh the counts when it completes.
  const handleUploadComplete = () => {
    refetchCats()
    refetchItems()
    refetchMenus()
  }

  const categoryCount = catData?.restaurant?.categories?.length ?? ''
  const itemCount = itemsData?.advancedRestaurantItems?.length ?? ''
  const menuCount = menusData?.advancedMenus?.length ?? ''
  const slug = catData?.restaurant?.slug
  // The count renders as a small round badge (orange on the active tab, via
  // the .Mui-selected rule on the Tabs sx below).
  const tabLabel = (name, count) => count !== '' ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {t(name)}
      <Box component="span" className="mw-count">{count}</Box>
    </Box>
  ) : t(name)

  const handleNewMenu = () => {
    setCurrentTab(0)
    if (openCreateRef.current) openCreateRef.current()
  }

  return (
    <ThemeProvider theme={menuWorkspaceTheme}>
    <Box sx={{ minHeight: '100vh', bgcolor: mw.color.pageBg, fontFamily: mw.font.body, color: mw.color.textPrimary }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 }, pt: { xs: 3, md: 4 }, pb: 6 }}>
        {/* Page header — plain, on the page background */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2.5, mb: 3.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box component="nav" aria-label="Breadcrumb" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, fontSize: 13, color: mw.color.textMuted, mb: 1.25 }}>
              {restaurantName && (
                <>
                  <Box component="span">{restaurantName}</Box>
                  <Box component="span" aria-hidden sx={{ color: mw.color.borderStrong }}>/</Box>
                </>
              )}
              <Box component="span" aria-current="page" sx={{ color: mw.color.textSecondary, fontWeight: 600 }}>{t('Menus')}</Box>
            </Box>
            <Typography component="h1" sx={{ ...displaySx(38), fontSize: { xs: 30, md: 38 } }}>{t('Menus')}</Typography>
            <Typography sx={{ fontSize: 15, color: mw.color.textMuted, mt: 1 }}>{t('Manage your restaurant menus, categories and items')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
            <Tooltip title={slug ? '' : t('This restaurant has no web address yet, so there is nothing to preview.')}>
              <span>
                <Button
                  startIcon={<VisibilityIcon />}
                  disabled={!slug}
                  onClick={() => setPreviewMenuId('')}
                  sx={pillBtnSx}>
                  {t('Preview')}
                </Button>
              </span>
            </Tooltip>
            <Button
              startIcon={<AutoAwesomeIcon />}
              onClick={() => { if (openAIRef.current) openAIRef.current() }}
              sx={aiBtnSx}>
              {t('AI Menu Upload')}
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={handleNewMenu}
              sx={pillBtnPrimarySx}>
              {t('New Menu')}
            </Button>
          </Box>
        </Box>

        {/* Tabs — a segmented control */}
        <Box sx={{ mb: 3.5 }}>
          <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{
              ...segmentedTabsSx,
              '& .mw-count': countBadgeSx(false),
              '& .Mui-selected .mw-count': { bgcolor: mw.color.accent, color: '#fff' },
            }}>
            <Tab label={tabLabel('Menus', menuCount)} />
            <Tab label={tabLabel('Categories', categoryCount)} />
            <Tab label={tabLabel('Items', itemCount)} />
            <Tab label={t('Modifier Studio')} />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box>
          {currentTab === 0 && <MenusTab onOpenCreate={openCreateRef} onOpenAI={openAIRef} onUploadComplete={handleUploadComplete} onPreview={slug ? id => setPreviewMenuId(String(id)) : null} />}
          {currentTab === 1 && <CategoriesTab />}
          {currentTab === 2 && <ItemsTab />}
          {currentTab === 3 && <AdvancedModifierStudio />}
        </Box>
      </Box>
      <MenuPreviewDialog
        open={previewMenuId !== null}
        onClose={() => setPreviewMenuId(null)}
        slug={slug}
        menus={menusData?.advancedMenus || []}
        initialMenuId={previewMenuId || ''}
      />
    </Box>
    </ThemeProvider>
  )
}

export default AdvancedMenus
